import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription, timer } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { AuthService } from 'src/app/core/services/auth.service';
import { MessageService } from 'src/app/core/services/message.service';
import { SystemNotificationService } from '../../../core/services/system-notification.service';

const SIXTY_SECONDS = 60000;

export interface CombinedCountData {
  messageCount?: number;
  notificationCount?: number;
  messageNotificationCount?: number;
}

@Component({
  selector: 'th-unread-message-count',
  templateUrl: './unread-message-count.component.html',
  styleUrls: ['./unread-message-count.component.scss']
})
export class UnreadMessageCountComponent implements OnInit, OnDestroy {
  @Input() hideText = false;

  lastNotificationDate: Date;
  unreadMessageCount = 0;

  private getUnreadMessageCountSubscription: Subscription;
  private messageCountUpdatedSubscription: Subscription;

  constructor(
    private authService: AuthService,
    private messageService: MessageService,
    private systemNotificationService: SystemNotificationService,
    private toastr: ToastrService,
    private router: Router
  ) {}

  ngOnInit() {
    this.getUnreadMessageCountSubscription = timer(0, SIXTY_SECONDS)
      .pipe(
        tap(() => {
          if (this.authService.isAuthenticated()) {
            this.getCombinedCount();
          }
        })
      )
      .subscribe();

    this.messageService.resetMessageCount();
    this.messageCountUpdatedSubscription = this.messageService.messageCountUpdated.subscribe(
      (count) => (this.unreadMessageCount = count)
    );
  }

  ngOnDestroy() {
    this.getUnreadMessageCountSubscription?.unsubscribe();
    this.messageCountUpdatedSubscription?.unsubscribe();
  }

  private getCombinedCount() {
    this.messageService
      .getCombinedCount(this.lastNotificationDate)
      .subscribe((countData: CombinedCountData) => this.handleCombinedResult(countData));
  }

  private handleCombinedResult(result: CombinedCountData) {
    if (result?.messageCount > 0) {
      this.unreadMessageCount = result.messageCount;
    }

    if (result?.messageNotificationCount > 0) {
      this.toastr
        .info(
          `<div>You have new message</div>
<div class="text-decoration-underline">Navigate to Messenger</div>`,
          undefined,
          { enableHtml: true }
        )
        .onTap.pipe(take(1))
        .subscribe(() => this.router.navigateByUrl('/private/messages'));
    }

    if (result?.notificationCount > 0) {
      this.systemNotificationService.setNotificationCount(
        result.notificationCount + this.systemNotificationService.getNotificationCount()
      );

      // Set this to prevent repeated "unread notification" toasts
      this.lastNotificationDate = new Date();

      const unreadNotificationCount = this.systemNotificationService.getNotificationCount();

      this.toastr
        .info(
          `<div>You have <b>${unreadNotificationCount}</b> new notification${unreadNotificationCount > 1 ? 's' : ''}.</div>
<div class="text-decoration-underline">Navigate to Notifications</div>`,
          undefined,
          { enableHtml: true }
        )
        .onTap.pipe(take(1))
        .subscribe(() => this.router.navigateByUrl('/private/notifications'));
    }
  }
}
